import { useGT } from 'gt-react';
import {
  keepPreviousData,
  skipToken,
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  EDI844Submission,
  EDI844SubmissionActivity,
  ListEDI844SubmissionsInput,
  ListEDI844SubmissionsResponse,
  GetEDI844SubmissionInput,
  GetEDI844SubmissionResponse,
  CreateEDI844SubmissionInput,
  UpdateEDI844SubmissionInput,
  UpdateEDI844SubmissionLineInput,
  BatchUpdateEDI844SubmissionLinesInput,
  ListEDI844SubmissionActivitiesInput,
  ListEDI844SubmissionActivitiesResponse,
  AddEDI844SubmissionCommentInput,
  GetEDI844BatchGroupsInput,
  GetEDI844BatchGroupsResponse,
  MarkAsAS400ConfirmedInput,
  MarkSubmissionAsFailedInput,
  ResubmitSubmissionInput,
  LinkSubmissionToVDMInput,
  LinkPreviousVdmInput,
  UnlinkPreviousVdmInput,
  UnlinkSubmissionFromVDMInput,
  DevSimulateVdmResponseInput,
  DownloadSubmissionBlobExcelInput,
} from '@erp/rpc';
import { useToast } from '@/context/ToastContext';
import { triggerExcelDownload } from './useE844sdTransactions';

/**
 * Hook for listing EDI844 submissions with optional filters
 */
export function useEDI844SubmissionsList(
  filters: ListEDI844SubmissionsInput = {},
) {
  const query = useQuery(
    orpc.shipAndDebit.edi844Submissions.list.queryOptions({
      input: filters,
    }),
  );

  const submissions = query.data?.submissions ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    submissions,
    pagination,
  } satisfies typeof query & {
    data?: ListEDI844SubmissionsResponse;
    submissions: EDI844Submission[];
    pagination: ListEDI844SubmissionsResponse['pagination'] | undefined;
  };
}

/**
 * Hook for fetching a single EDI844 submission by ID
 */
export function useEDI844Submission(
  id: string | undefined,
  options: Omit<GetEDI844SubmissionInput, 'id'> = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi844Submissions.get.queryOptions({
      input: id ? { id, ...options } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    submission: query.data ?? null,
  } satisfies typeof query & {
    data?: GetEDI844SubmissionResponse;
    submission: GetEDI844SubmissionResponse | null;
  };
}

/**
 * Hook for creating a new EDI844 submission
 */
export function useCreateEDI844Submission() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: CreateEDI844SubmissionInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.create(input);
    },
    onSuccess: () => {
      // Invalidate submissions list to refetch
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions', 'list'],
      });
      showToast({
        message: 'Submission created successfully',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to create submission',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for updating an EDI844 submission
 */
export function useUpdateEDI844Submission() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: UpdateEDI844SubmissionInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.update(input);
    },
    onSuccess: (data) => {
      // Invalidate both list and specific submission
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions', 'get', data.id],
      });
      showToast({
        message: 'Submission updated successfully',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to update submission',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for updating a submission line
 */
export function useUpdateEDI844SubmissionLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateEDI844SubmissionLineInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.updateLine(input);
    },
    onSuccess: () => {
      // Invalidate submissions to refetch with updated line data
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions'],
      });
    },
  });
}

/**
 * Hook for batch updating submission lines (e.g., processing 849 responses)
 */
export function useBatchUpdateEDI844SubmissionLines() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: BatchUpdateEDI844SubmissionLinesInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.batchUpdateLines(input);
    },
    onSuccess: (data) => {
      // Invalidate submissions to refetch
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions'],
      });
      showToast({
        message: `Updated ${data.updatedLines.length} lines`,
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to update lines',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for listing submission activities (timeline)
 */
export function useEDI844SubmissionActivities({
  submissionId,
  filters = {},
  enabled = true,
}: {
  submissionId: string | undefined;
  filters?: Omit<ListEDI844SubmissionActivitiesInput, 'submissionId'>;
  enabled?: boolean;
}) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi844Submissions.listActivities.queryOptions({
      input: submissionId ? { submissionId, ...filters } : skipToken,
    }),
    enabled: Boolean(submissionId) && enabled,
  });

  const activities = query.data?.activities ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    activities,
    pagination,
  } satisfies typeof query & {
    data?: ListEDI844SubmissionActivitiesResponse;
    activities: EDI844SubmissionActivity[];
    pagination:
      ListEDI844SubmissionActivitiesResponse['pagination'] | undefined;
  };
}

/**
 * Hook for adding a comment to a submission
 */
export function useAddEDI844SubmissionComment() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: AddEDI844SubmissionCommentInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.addComment(input);
    },
    onSuccess: (_, variables) => {
      // Invalidate activities for this submission
      queryClient.invalidateQueries({
        queryKey: [
          'shipAndDebit',
          'edi844Submissions',
          'listActivities',
          variables.submissionId,
        ],
      });
      showToast({
        message: 'Comment added',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to add comment',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for marking submissions as AS400 confirmed
 */
export function useMarkAsAS400Confirmed() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: MarkAsAS400ConfirmedInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.markAsAS400Confirmed(
        input,
      );
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['shipAndDebit', 'edi844Submissions'],
      });
      showToast({
        message: `Marked ${data.updatedCount} submission(s) as AS400 confirmed`,
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to mark as AS400 confirmed',
        color: 'error',
      });
    },
  });
}

/**
 * Record an OLD VDM on a resubmitted 844 ("the previous VDM of this submission
 * was X"). History only — it cannot change what was sent to AS400.
 */
export function useLinkPreviousVdm() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const gt = useGT();

  return useMutation({
    mutationFn: async (input: LinkPreviousVdmInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.linkPreviousVdm(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      showToast({
        message: gt('Previous VDM linked', {
          context: 'toast after recording an old VDM on an 844 submission',
        }),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message:
          error.message ||
          gt('Failed to link previous VDM', {
            context: 'toast when recording an old VDM on an 844 fails',
          }),
        color: 'error',
      });
    },
  });
}

/** Remove a previous-VDM link from a submission. */
export function useUnlinkPreviousVdm() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const gt = useGT();

  return useMutation({
    mutationFn: async (input: UnlinkPreviousVdmInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.unlinkPreviousVdm(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      showToast({
        message: gt('Previous VDM unlinked', {
          context:
            'toast after removing an old VDM link from an 844 submission',
        }),
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message:
          error.message ||
          gt('Failed to unlink previous VDM', {
            context: 'toast when removing an old VDM link from an 844 fails',
          }),
        color: 'error',
      });
    },
  });
}

/**
 * Hook for linking a submission to a VDM
 */
export function useLinkSubmissionToVDM() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: LinkSubmissionToVDMInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.linkVdm(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.key(),
      });
      showToast({
        message: 'Submission linked to VDM',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to link submission to VDM',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for unlinking a submission from its VDM
 */
export function useUnlinkSubmissionFromVDM() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: UnlinkSubmissionFromVDMInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.unlinkVdm(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.key(),
      });
      showToast({
        message: 'Submission unlinked from VDM',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to unlink submission from VDM',
        color: 'error',
      });
    },
  });
}

/**
 * Hook for getting batch groups (for creating new submissions)
 */
export function useEDI844BatchGroups(
  filters: GetEDI844BatchGroupsInput = {},
  enabled = true,
) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi844Submissions.getBatchGroups.queryOptions({
      input: filters,
    }),
    enabled,
  });

  const groups = query.data?.groups ?? [];

  return {
    ...query,
    groups,
  } satisfies typeof query & {
    data?: GetEDI844BatchGroupsResponse;
    groups: GetEDI844BatchGroupsResponse['groups'];
  };
}

/**
 * Hook for summary stats (top cards on 844 submissions page).
 * Independent of pagination so cards stay stable when paging.
 */
export function useEDI844SummaryStats() {
  return useQuery(
    orpc.shipAndDebit.edi844Submissions.getSummaryStats.queryOptions({
      input: {},
    }),
  );
}

/**
 * Hook for getting supplier stats (charts on 844 submissions page)
 */
export function useEDI844SupplierStats(params?: {
  deliveryMethod?: 'edi' | 'email';
  startDate?: string;
  endDate?: string;
}) {
  return useQuery({
    ...orpc.shipAndDebit.edi844Submissions.getSupplierStats.queryOptions({
      input: {
        deliveryMethod: params?.deliveryMethod,
        startDate: params?.startDate,
        endDate: params?.endDate,
      },
    }),
    placeholderData: keepPreviousData,
  });
}

export function useDevSimulateVdmResponse() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: DevSimulateVdmResponseInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.devSimulateVdmResponse(
        input,
      );
    },
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.vdms.key(),
      });
      const msg =
        variables.outcome === 'confirmed'
          ? `VDM confirmed (ADM${data.vdmNumber}) — email sent`
          : 'VDM marked as failed';
      showToast({ message: msg, color: 'success' });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to simulate VDM response',
        color: 'error',
      });
    },
  });
}

export function useMarkSubmissionAsFailed() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: MarkSubmissionAsFailedInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.markAsFailed(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.e844sdTransactions.key(),
      });
      showToast({
        message: 'Submission marked as failed — transactions are now editable',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to mark submission as failed',
        color: 'error',
      });
    },
  });
}

export function useResubmitSubmission() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: ResubmitSubmissionInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.resubmit(input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.edi844Submissions.key(),
      });
      void queryClient.invalidateQueries({
        queryKey: orpc.shipAndDebit.e844sdTransactions.key(),
      });
      showToast({
        message: 'Submission resubmitted with new batch number',
        color: 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to resubmit submission',
        color: 'error',
      });
    },
  });
}

export function useSubmissionBlobData(
  submissionId: string | undefined,
  enabled: boolean,
) {
  const query = useQuery({
    ...orpc.shipAndDebit.edi844Submissions.getBlobData.queryOptions({
      input: submissionId ? { submissionId } : skipToken,
    }),
    enabled: Boolean(submissionId) && enabled,
  });

  return {
    columns: query.data?.columns,
    rows: query.data?.rows,
    blobKey: query.data?.blobKey,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useDownloadSubmissionBlobExcel() {
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (input: DownloadSubmissionBlobExcelInput) => {
      return rpcClient.shipAndDebit.edi844Submissions.downloadBlobExcel(input);
    },
    onSuccess: (data) => triggerExcelDownload(data.excel, data.filename),
    onError: (error: Error) => {
      showToast({
        message: error.message || 'Failed to download Excel',
        title: 'Error',
        color: 'error',
        icon: 'error',
      });
    },
  });
}
